/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Form, Input, Select, DatePicker, message, Tooltip } from 'antd';
import {
	ArrowLeftOutlined,
	UploadOutlined,
	CloseOutlined,
	FileTextOutlined,
	EyeOutlined,
	ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Box, Text } from 'atoms';
import { getSignedUrl } from 'store/commonSlice';
import { OWNERSHIP_TYPE_OPTIONS } from 'componentsv2/forms/vendor-steps/VehicleInformationStep.constants';
import { searchVendorV2 } from 'store/vendorSliceV2';
import { searchVehicleTypesV2, createVehicleV2 } from 'store/vehicleSliceV2';
import { bulkUploadVehicles } from 'store/bulkUploadSlice';
import DocumentViewerModal from 'componentsv2/common/modals/DocumentViewerModal';
import BulkUploadModal from 'componentsv2/modals/BulkUploadModal';
import EditVehicleModal from './EditVehicleModal';
import {
	VALID_TILL_DATE_MESSAGE,
	VEHICLE_NUMBER_PATTERN,
	VEHICLE_NUMBER_MESSAGE,
	generateBulkUploadTemplate,
} from './CreateVehiclePageV2.constants';
import { formatFileSize, transformVehicleToAPIFormat } from './CreateVehiclePageV2.utils';
import {
	PageContainer,
	ContentWrapper,
	PageHeader,
	BackButton,
	PageTitle,
	PageSubtitle,
	HeaderActions,
	BulkUploadButton,
	FormSection,
	SectionTitle,
	StyledForm,
	PrimaryBrowseFileButton,
	LightBrowseFileButton,
	AddVehicleButtonWrapper,
	AddVehicleButton,
	TableSection,
	StyledTable,
	FooterActions,
	CancelButton,
	SubmitButton,
	HiddenFileInput,
	FileDisplayContainer,
	FileInfo,
	FileIcon,
	PendingDocumentStatus,
	RemoveFileButton,
	FormGrid,
	InlineRow,
	UploadButtonWrapper,
	DatePickerWrapper,
	SkipDocumentUploadRow,
	SkipDocumentCheckbox,
	SkipDocumentInfoIcon,
} from './CreateVehiclePageV2.styles';

const { Option } = Select;

export default function CreateVehiclePageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const ownershipType = Form.useWatch('ownership_type', form);
	const gpsInstalled = Form.useWatch('gps_installed', form);

	// Form state
	const [vendorOptions, setVendorOptions] = useState([]);
	const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
	const [vehicleDetails, setVehicleDetails] = useState([]);
	const [skipDocumentUpload, setSkipDocumentUpload] = useState(false);

	// File upload refs
	const rcDocFileInputRef = useRef(null);
	const insuranceDocFileInputRef = useRef(null);
	const pucDocFileInputRef = useRef(null);
	const consentFileInputRef = useRef(null);

	// File upload states
	const [uploading, setUploading] = useState({
		rc_doc: false,
		insurance: false,
		puc_doc: false,
		consent_letter: false,
	});

	// File keys (S3 keys after upload)
	const [fileKeys, setFileKeys] = useState({
		rc_doc_key: null,
		insurance_key: null,
		puc_doc_key: null,
		consent_letter_key: null,
	});

	// File names for display
	const [fileNames, setFileNames] = useState({
		rc_doc: null,
		insurance: null,
		puc_doc: null,
		consent_letter: null,
	});
	const isPucExpiryDateRequired = !skipDocumentUpload && Boolean(fileKeys.puc_doc_key);

	// File sizes for display
	const [fileSizes, setFileSizes] = useState({
		rc_doc: null,
		insurance: null,
		puc_doc: null,
		consent_letter: null,
	});

	// Edit modal state
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [selectedVehicle, setSelectedVehicle] = useState(null);

	// Document viewer modal state
	const [documentModalVisible, setDocumentModalVisible] = useState(false);
	const [documentUrl, setDocumentUrl] = useState('');
	const [documentName, setDocumentName] = useState('');
	const [documentType, setDocumentType] = useState(null);

	// Bulk upload state
	const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
	const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
	const [bulkUploadTemplateUrl, setBulkUploadTemplateUrl] = useState('');
	const [uploadedS3Key, setUploadedS3Key] = useState(null); // Store S3 key after upload
	const [uploadedFileName, setUploadedFileName] = useState(null); // Store file name for display

	// Constants
	const BULK_UPLOAD_MESSAGE_KEY = 'bulk-upload';

	// Shared error handler for API calls
	const setErrors = (errors) => {
		// Handle string errors (e.g., "Vehicle number already exists")
		if (typeof errors === 'string') {
			message.error(errors);
			return;
		}

		// Handle object errors (field-specific errors)
		if (errors && typeof errors === 'object') {
			Object.keys(errors).forEach((key) => {
				message.error(errors[key] || 'Validation error');
			});
		}
	};

	// Fetch vendors for dropdown
	useEffect(() => {
		dispatch(
			searchVendorV2({
				filter_by: { is_active: true },
				pagination: { page: 1, per_page: 1000 },
			})
		).then((response) => {
			const vendors = (response?.rows || []).map((vendor) => ({
				value: vendor.id,
				label: vendor.vendor_name || vendor.name || `Vendor ${vendor.id}`,
			}));
			setVendorOptions(vendors);
		});
	}, [dispatch]);

	// Fetch vehicle types for dropdown
	useEffect(() => {
		dispatch(
			searchVehicleTypesV2({
				filter_by: { is_active: true },
				pagination: { page: 1, per_page: 1000 },
			})
		).then((response) => {
			const types = (response?.rows || []).map((type) => ({
				value: type.id,
				label: type.vehicle_type_name || `Type ${type.id}`,
			}));
			setVehicleTypeOptions(types);
		});
	}, [dispatch]);

	// Generate bulk upload CSV template on mount
	useEffect(() => {
		const templateUrl = generateBulkUploadTemplate();
		setBulkUploadTemplateUrl(templateUrl);

		// Cleanup: Revoke the object URL when component unmounts to avoid memory leaks
		return () => {
			if (templateUrl) {
				URL.revokeObjectURL(templateUrl);
			}
		};
	}, []);

	// Handle file upload
	const handleFileUpload = async (file, fieldName) => {
		if (!file) {
			message.error('Please select a file');
			return false;
		}

		// File validation
		const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
		const maxSizeMB = 5;

		if (!allowedTypes.includes(file.type)) {
			message.error('You can only upload PDF, PNG, JPG, JPEG files!');
			return false;
		}
		if (file.size / 1024 / 1024 > maxSizeMB) {
			message.error(`File must be smaller than ${maxSizeMB}MB!`);
			return false;
		}

		// Store file name and size for display
		setFileNames((prev) => ({ ...prev, [fieldName]: file.name }));
		setFileSizes((prev) => ({ ...prev, [fieldName]: file.size }));

		setUploading((prev) => ({ ...prev, [fieldName]: true }));

		try {
			// Step 1: Get signed URL
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'vendor',
						keys: [file.name],
					},
				})
			);

			const fileData = signedUrlResponse?.data?.[0];
			if (!fileData?.url || !fileData?.filename) {
				throw new Error('Failed to get signed URL');
			}

			// Step 2: Upload file to S3
			await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			});

			// Step 3: Store file key
			const keyField = `${fieldName}_key`;
			setFileKeys((prev) => ({ ...prev, [keyField]: fileData.filename }));
			form.setFieldsValue({ [keyField]: fileData.filename });

			message.success('File uploaded successfully');
			return true;
		} catch (error) {
			console.error('File upload error:', error);
			message.error('Failed to upload file');
			// Clear file name and size on error
			setFileNames((prev) => ({ ...prev, [fieldName]: null }));
			setFileSizes((prev) => ({ ...prev, [fieldName]: null }));
			return false;
		} finally {
			setUploading((prev) => ({ ...prev, [fieldName]: false }));
		}
	};

	// Handle file input change
	const handleFileChange = async (event, fieldName) => {
		const file = event.target.files?.[0];
		if (file) {
			await handleFileUpload(file, fieldName);
		}
		// Reset input
		if (event.target) {
			event.target.value = '';
		}
	};

	// Handle browse file click
	const handleBrowseFile = (fieldName, ref) => {
		ref.current?.click();
	};

	// Handle file removal
	const handleRemoveFile = (fieldName) => {
		const keyField = `${fieldName}_key`;
		setFileKeys((prev) => ({ ...prev, [keyField]: null }));
		setFileNames((prev) => ({ ...prev, [fieldName]: null }));
		setFileSizes((prev) => ({ ...prev, [fieldName]: null }));
		form.setFieldsValue({ [keyField]: null });
		message.success('File removed');
	};

	const clearUploadedDocumentState = () => {
		setFileKeys({
			rc_doc_key: null,
			insurance_key: null,
			puc_doc_key: null,
			consent_letter_key: null,
		});
		setFileNames({
			rc_doc: null,
			insurance: null,
			puc_doc: null,
			consent_letter: null,
		});
		setFileSizes({
			rc_doc: null,
			insurance: null,
			puc_doc: null,
			consent_letter: null,
		});
		form.setFieldsValue({
			rc_doc_key: null,
			rc_expiry_date: null,
			insurance_key: null,
			insurance_validity_date: null,
			puc_doc_key: null,
			puc_expiry_date: null,
			consent_letter_key: null,
			ownership_type: null,
			gps_installed: null,
			gps_provider_name: null,
		});
	};

	// Handle add vehicle to table
	const handleAddVehicle = () => {
		// Get all form values to check ownership type
		const formValues = form.getFieldsValue();
		const fieldsToValidate = ['vendor_id', 'vehicle_type_id', 'vehicle_number'];

		// Only validate document and GPS fields if skip document upload is NOT checked
		if (!skipDocumentUpload) {
			fieldsToValidate.push(
				'rc_doc_key',
				'rc_expiry_date',
				'insurance_key',
				'insurance_validity_date',
				'gps_installed'
			);

			// If ownership type is Aggregator, consent_letter_key is required
			if (formValues.ownership_type === 'Aggregator') {
				fieldsToValidate.push('consent_letter_key');
			}

			if (fileKeys.puc_doc_key) {
				fieldsToValidate.push('puc_expiry_date');
			}

			if (formValues.gps_installed === true) {
				fieldsToValidate.push('gps_provider_name');
			}
		}

		form.validateFields(fieldsToValidate)
			.then((values) => {
				// Get ownership_type directly from form as it might not be in validated fields
				const ownershipTypeValue = form.getFieldValue('ownership_type');
				const pucExpiryDate = form.getFieldValue('puc_expiry_date');

				const newVehicle = {
					key: Date.now(),
					vendor_id: values.vendor_id,
					vendor_name:
						vendorOptions.find((v) => v.value === values.vendor_id)?.label || '',
					vehicle_type_id: values.vehicle_type_id,
					vehicle_type_name:
						vehicleTypeOptions.find((v) => v.value === values.vehicle_type_id)?.label ||
						'',
					vehicle_number: values.vehicle_number,
					rc_document_key: fileKeys.rc_doc_key,
					rc_expiry_date: values.rc_expiry_date,
					insurance_document_key: fileKeys.insurance_key,
					insurance_validity_date: values.insurance_validity_date,
					puc_document_key: fileKeys.puc_doc_key,
					puc_expiry_date: pucExpiryDate,
					consent_letter_key: fileKeys.consent_letter_key,
					ownership_type: ownershipTypeValue || null,
					gps_installed: values.gps_installed,
					gps_provider_name: values.gps_provider_name,
					skip_document_upload: skipDocumentUpload,
				};

				setVehicleDetails([...vehicleDetails, newVehicle]);
				setSkipDocumentUpload(false);
				form.resetFields([
					'vendor_id',
					'vehicle_type_id',
					'vehicle_number',
					'rc_doc_key',
					'rc_expiry_date',
					'insurance_key',
					'insurance_validity_date',
					'puc_doc_key',
					'puc_expiry_date',
					'consent_letter_key',
					'ownership_type',
					'gps_installed',
					'gps_provider_name',
				]);
				setFileKeys({
					rc_doc_key: null,
					insurance_key: null,
					puc_doc_key: null,
					consent_letter_key: null,
				});
				setFileNames({
					rc_doc: null,
					insurance: null,
					puc_doc: null,
					consent_letter: null,
				});
				setFileSizes({
					rc_doc: null,
					insurance: null,
					puc_doc: null,
					consent_letter: null,
				});
				message.success('Vehicle added to the list');
			})
			.catch((error) => {
				console.error('Validation failed:', error);
			});
	};

	// Handle view/edit vehicle
	const handleViewVehicle = (record) => {
		setSelectedVehicle(record);
		setEditModalVisible(true);
	};

	// Handle update vehicle from modal
	const handleUpdateVehicle = (updatedVehicle) => {
		setVehicleDetails((prev) =>
			prev.map((vehicle) => (vehicle.key === selectedVehicle.key ? updatedVehicle : vehicle))
		);
		message.success('Vehicle updated successfully');
		setSelectedVehicle(null);
	};

	// Handle remove vehicle from modal
	const handleRemoveVehicle = (vehicleToRemove) => {
		setVehicleDetails((prev) => prev.filter((v) => v.key !== vehicleToRemove.key));
		message.success('Vehicle removed successfully');
		setSelectedVehicle(null);
	};

	// Handle edit modal close
	const handleEditModalClose = () => {
		setEditModalVisible(false);
		setSelectedVehicle(null);
	};

	// Handle document view
	const handleViewDocument = async (fileKey, docName) => {
		if (!fileKey) {
			message.warning('Document not available');
			return;
		}

		setDocumentModalVisible(false);

		try {
			const response = await dispatch(
				getSignedUrl({
					url_type: 'view',
					data: {
						entity: 'vendor',
						keys: [fileKey],
					},
				})
			);

			if (response?.data && response.data[0]?.url) {
				setDocumentUrl(response.data[0].url);
				setDocumentName(docName);
				// Detect document type from file extension
				const extension = fileKey.split('.').pop()?.toLowerCase();
				if (extension === 'pdf') {
					setDocumentType('pdf');
				} else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
					setDocumentType('image');
				} else {
					setDocumentType(null);
				}
				setDocumentModalVisible(true);
			} else {
				message.error('Failed to get document URL');
			}
		} catch (error) {
			console.error('Error viewing document:', error);
			message.error('Failed to view document');
		}
	};

	// Handle document modal close
	const handleDocumentModalClose = () => {
		setDocumentModalVisible(false);
		setDocumentUrl('');
		setDocumentName('');
		setDocumentType(null);
	};

	// Handle bulk upload button click
	const handleBulkUpload = () => {
		setBulkUploadModalVisible(true);
	};

	// Handle bulk upload modal close
	const handleBulkUploadClose = () => {
		setBulkUploadModalVisible(false);
		setUploadedS3Key(null);
		setUploadedFileName(null);
		setBulkUploadLoading(false);
	};

	// Handle file upload to S3 (called when file is selected in the modal)
	const handleBulkUploadFileToS3 = async (file) => {
		console.log('📁 [Bulk Upload] Step 1: File selected, uploading to S3...', {
			fileName: file.name,
			fileSize: file.size,
			fileType: file.type,
		});

		setBulkUploadLoading(true);
		message.loading({
			content: 'Uploading file to S3...',
			key: BULK_UPLOAD_MESSAGE_KEY,
		});

		try {
			// Step 1: Get signed URL for S3 upload
			console.log('🔑 [Bulk Upload] Getting signed URL for S3 upload...');
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'vehicle_bulk_upload_v2',
						keys: [file.name],
						content_type: 'text/csv',
					},
				})
			);

			console.log('📊 [Bulk Upload] Signed URL response:', {
				hasResponse: !!signedUrlResponse,
				status: signedUrlResponse?.status,
				hasData: !!signedUrlResponse?.data,
			});

			if (!signedUrlResponse || signedUrlResponse.status !== 'success') {
				throw new Error('Failed to get S3 upload URL');
			}

			const uploadData = signedUrlResponse.data?.[0];
			if (!uploadData?.url || !uploadData?.key) {
				throw new Error('Invalid S3 upload URL response');
			}

			const { url: uploadUrl, key: s3Key } = uploadData;
			console.log('📤 [Bulk Upload] Step 2: Uploading to S3...', { s3Key });

			// Step 2: Upload file to S3
			const uploadResponse = await fetch(uploadUrl, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type || 'text/csv',
				},
			});

			if (!uploadResponse.ok) {
				throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
			}

			console.log('✅ [Bulk Upload] File uploaded to S3 successfully', { s3Key });

			// Store S3 key for later submission
			setUploadedS3Key(s3Key);
			setUploadedFileName(file.name);

			message.success({
				content: `${file.name} uploaded successfully. Click Submit to process vehicles.`,
				key: BULK_UPLOAD_MESSAGE_KEY,
				duration: 3,
			});
		} catch (error) {
			console.error('❌ [Bulk Upload] Error uploading file to S3:', error);
			message.error({
				content: error.message || 'Failed to upload file. Please try again.',
				key: BULK_UPLOAD_MESSAGE_KEY,
				duration: 5,
			});
			setUploadedS3Key(null);
			setUploadedFileName(null);
		} finally {
			setBulkUploadLoading(false);
		}
	};

	// Helper: Handle successful bulk upload response
	const handleBulkUploadSuccess = (response) => {
		const { data, message: apiMessage } = response;
		const { success_count, error_count, total_count, download_url } = data || {};

		console.log('✅ [Bulk Upload] Bulk upload completed:', {
			success_count,
			error_count,
			total_count,
			hasResultFile: !!download_url,
			apiMessage,
		});

		if (error_count > 0) {
			// Partial success - some rows failed
			const successMsg = total_count
				? `Bulk upload completed: ${success_count} out of ${total_count} succeeded (${error_count} failed)`
				: `Bulk upload completed: ${success_count} succeeded, ${error_count} failed`;

			message.warning({
				content: download_url
					? `${successMsg}. Click to download result CSV with error details.`
					: successMsg,
				key: BULK_UPLOAD_MESSAGE_KEY,
				duration: 10,
			});

			if (download_url) {
				console.log('📥 [Bulk Upload] Opening result CSV:', download_url);
				// Delay slightly to ensure message is visible first
				setTimeout(() => {
					window.open(download_url, '_blank');
				}, 500);
			}
		} else {
			// Full success
			const vehicleText = success_count !== 1 ? 'vehicles' : 'vehicle';
			const successMsg = total_count
				? `Successfully uploaded all ${total_count} ${vehicleText}`
				: `Successfully uploaded ${success_count} ${vehicleText}`;

			message.success({
				content: apiMessage || successMsg,
				key: BULK_UPLOAD_MESSAGE_KEY,
				duration: 5,
			});
		}

		// Close modal and navigate
		setBulkUploadModalVisible(false);
		setTimeout(() => {
			console.log('🔄 [Bulk Upload] Navigating to vehicle list...');
			navigate('/vehicles');
		}, 1500);
	};

	// Handle bulk upload submission (process vehicles via API using S3 key)
	const handleBulkUploadSubmit = async () => {
		console.log('🚀 [Bulk Upload] Step 3: Processing vehicles via API...', {
			hasS3Key: !!uploadedS3Key,
			s3Key: uploadedS3Key,
			fileName: uploadedFileName,
		});

		// Validate that file has been uploaded to S3
		if (!uploadedS3Key) {
			console.error('❌ [Bulk Upload] No S3 key available. Please upload a file first.');
			message.error('Please upload a file before submitting.');
			return;
		}

		setBulkUploadLoading(true);

		message.loading({
			content: 'Processing vehicles...',
			key: BULK_UPLOAD_MESSAGE_KEY,
		});

		try {
			console.log('📤 [Bulk Upload] Calling bulk upload API with S3 key...');

			// Call bulk upload API with S3 key
			const response = await dispatch(
				bulkUploadVehicles({
					s3_key: uploadedS3Key,
					setErrors,
				})
			);

			console.log('📊 [Bulk Upload] Checking response:', {
				hasResponse: !!response,
				responseType: typeof response,
				status: response?.status,
				response,
			});

			// Check if response is valid
			if (!response) {
				console.error('❌ [Bulk Upload] Response is null or undefined');
				throw new Error('No response received from server. Please try again.');
			}

			// Handle response
			if (response.status === 'success') {
				handleBulkUploadSuccess(response);
			} else if (response.status === 'error') {
				const errorCode = response.error?.code || 'ERROR';
				const errorMessage = response.error?.message || 'Failed to process bulk upload';
				console.error('❌ [Bulk Upload] API returned error:', {
					errorCode,
					errorMessage,
					fullError: response.error,
				});

				// Display user-friendly error messages
				let displayMessage = errorMessage;
				if (errorCode === 'ENDPOINT_NOT_FOUND') {
					displayMessage =
						'⚠️ Bulk upload feature is not available. Please contact your administrator to enable the /api/v2/upload endpoint.';
				} else if (errorCode === 'UNAUTHORIZED') {
					displayMessage =
						'🔒 You do not have permission to upload vehicles in bulk. Please contact your administrator.';
				}

				throw new Error(displayMessage);
			} else {
				console.error('❌ [Bulk Upload] Unexpected response format:', response);
				throw new Error(
					response?.message || 'Unexpected response from server. Please contact support.'
				);
			}
		} catch (error) {
			console.error('❌ [Bulk Upload] Error occurred:', {
				message: error.message,
				stack: error.stack,
				error,
			});

			// Longer duration for important error messages
			const errorDuration = error.message?.includes('administrator') ? 10 : 7;

			message.error({
				content: error.message || 'Failed to upload vehicles. Please try again.',
				key: BULK_UPLOAD_MESSAGE_KEY,
				duration: errorDuration,
			});
		} finally {
			console.log('🏁 [Bulk Upload] Upload process finished');
			setBulkUploadLoading(false);
		}
	};

	// Handle form submit
	const handleSubmit = async () => {
		if (vehicleDetails.length === 0) {
			message.warning('Please add at least one vehicle');
			return;
		}

		try {
			const promises = vehicleDetails.map((vehicle) => {
				const req_data = transformVehicleToAPIFormat(vehicle);
				return dispatch(createVehicleV2({ req_data, setErrors }));
			});

			const results = await Promise.allSettled(promises);

			const isSuccess = (r) => r.status === 'fulfilled' && r.value?.status === 'success';
			const successful = results.filter(isSuccess).length;
			const failed = results.length - successful;

			if (successful > 0) {
				message.success(`${successful} vehicle(s) created successfully`);
			}

			if (failed > 0) {
				message.error(`${failed} vehicle(s) failed to create`);
			}

			if (successful === vehicleDetails.length) {
				setVehicleDetails([]);
				setTimeout(() => {
					navigate('/vehicles');
				}, 1500);
			}
		} catch (error) {
			console.error('Error creating vehicles:', error);
			message.error('Failed to create vehicles. Please try again.');
		}
	};

	// Table columns
	const tableColumns = [
		{
			title: 'Vendor Name',
			dataIndex: 'vendor_name',
			key: 'vendor_name',
		},
		{
			title: 'Vehicle Type',
			dataIndex: 'vehicle_type_name',
			key: 'vehicle_type_name',
		},
		{
			title: 'vehicle Number',
			dataIndex: 'vehicle_number',
			key: 'vehicle_number',
		},
		{
			title: 'RC Document',
			dataIndex: 'rc_document_key',
			key: 'rc_document',
			render: (key, record) => {
				if (key) {
					return (
						<FileTextOutlined
							style={{
								fontSize: 18,
								color: '#1890ff',
								cursor: 'pointer',
							}}
							onClick={() =>
								handleViewDocument(key, `RC Document - ${record.vehicle_number}`)
							}
						/>
					);
				}

				if (record.skip_document_upload) {
					return (
						<PendingDocumentStatus>
							<ExclamationCircleOutlined style={{ fontSize: 14 }} />
							<Text style={{ color: 'inherit', fontSize: 12, fontWeight: 600 }}>
								Pending
							</Text>
						</PendingDocumentStatus>
					);
				}

				return '-';
			},
		},
		{
			title: 'Insurance',
			dataIndex: 'insurance_document_key',
			key: 'insurance',
			render: (key, record) => {
				if (key) {
					return (
						<FileTextOutlined
							style={{
								fontSize: 18,
								color: '#1890ff',
								cursor: 'pointer',
							}}
							onClick={() =>
								handleViewDocument(
									key,
									`Insurance Document - ${record.vehicle_number}`
								)
							}
						/>
					);
				}

				if (record.skip_document_upload) {
					return (
						<PendingDocumentStatus>
							<ExclamationCircleOutlined style={{ fontSize: 14 }} />
							<Text style={{ color: 'inherit', fontSize: 12, fontWeight: 600 }}>
								Pending
							</Text>
						</PendingDocumentStatus>
					);
				}

				return '-';
			},
		},
		{
			title: 'Ownership Type',
			dataIndex: 'ownership_type',
			key: 'ownership_type',
			render: (type) => type || '-',
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_, record) => (
				<EyeOutlined
					style={{ fontSize: 18, color: '#1890ff', cursor: 'pointer' }}
					onClick={() => handleViewVehicle(record)}
				/>
			),
		},
	];

	return (
		<PageContainer>
			<ContentWrapper>
				<PageHeader>
					<BackButton onClick={() => navigate('/vehicles')}>
						<ArrowLeftOutlined style={{ fontSize: 18, color: '#64748b' }} />
					</BackButton>
					<Box>
						<PageTitle>Onboarding Vehicle</PageTitle>
						<PageSubtitle>Add and onboard new Vehicles</PageSubtitle>
					</Box>
					<HeaderActions>
						<BulkUploadButton icon={<UploadOutlined />} onClick={handleBulkUpload}>
							Bulk Upload
						</BulkUploadButton>
					</HeaderActions>
				</PageHeader>

				<FormSection>
					<SectionTitle>Add Vehicle</SectionTitle>
					<StyledForm form={form} layout="vertical">
						<FormGrid>
							{/* Row 1: Select Vendor | Vehicle Number */}
							<Form.Item
								name="vendor_id"
								label="Select Vendor"
								rules={[{ required: true, message: 'Please select vendor' }]}
							>
								<Select
									placeholder="Select"
									showSearch
									filterOption={(input, option) =>
										(option?.children ?? '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
								>
									{vendorOptions.map((vendor) => (
										<Option key={vendor.value} value={vendor.value}>
											{vendor.label}
										</Option>
									))}
								</Select>
							</Form.Item>
							<Form.Item
								name="vehicle_number"
								label="Vehicle Number"
								rules={[
									{ required: true, message: 'Please enter vehicle number' },
									{
										pattern: VEHICLE_NUMBER_PATTERN,
										message: VEHICLE_NUMBER_MESSAGE,
									},
									{
										transform: (value) => value?.toUpperCase(),
									},
								]}
								normalize={(value) => value?.toUpperCase()}
							>
								<Input
									placeholder="Eg : DL01AB1234 or DL 01 AB 1234"
									style={{ textTransform: 'uppercase' }}
								/>
							</Form.Item>
							{/* Row 2: Vehicle Type | Insurance Doc Upload + Valid Till */}
							<Form.Item
								name="vehicle_type_id"
								label="Vehicle Type"
								rules={[{ required: true, message: 'Please select vehicle type' }]}
							>
								<Select
									placeholder="Select"
									showSearch
									filterOption={(input, option) =>
										(option?.children ?? '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
								>
									{vehicleTypeOptions.map((type) => (
										<Option key={type.value} value={type.value}>
											{type.label}
										</Option>
									))}
								</Select>
							</Form.Item>
							<Form.Item
								name="insurance_key"
								label="Insurance Doc Upload"
								rules={[
									{
										required: !skipDocumentUpload,
										message: 'Please upload insurance document',
									},
								]}
							>
								<InlineRow>
									<UploadButtonWrapper>
										<PrimaryBrowseFileButton
											icon={<UploadOutlined />}
											onClick={() =>
												handleBrowseFile(
													'insurance',
													insuranceDocFileInputRef
												)
											}
											loading={uploading.insurance}
											disabled={skipDocumentUpload}
										>
											Browse File
										</PrimaryBrowseFileButton>
										{fileNames.insurance && (
											<FileDisplayContainer>
												<FileInfo>
													<FileIcon />
													<Text
														style={{
															fontSize: 14,
															color: '#374151',
														}}
													>
														Fileupload.Pdf{' '}
														{fileSizes.insurance &&
															formatFileSize(fileSizes.insurance)}
													</Text>
												</FileInfo>
												<RemoveFileButton
													onClick={() => handleRemoveFile('insurance')}
												>
													<CloseOutlined />
												</RemoveFileButton>
											</FileDisplayContainer>
										)}
										<HiddenFileInput
											type="file"
											ref={insuranceDocFileInputRef}
											accept=".pdf,.png,.jpg,.jpeg"
											onChange={(e) => handleFileChange(e, 'insurance')}
										/>
									</UploadButtonWrapper>
									<DatePickerWrapper>
										<Form.Item
											name="insurance_validity_date"
											label="Valid Till"
											rules={[
												{
													required: !skipDocumentUpload,
													message: VALID_TILL_DATE_MESSAGE,
												},
											]}
											style={{ marginBottom: 0 }}
										>
											<DatePicker
												style={{ width: '100%' }}
												placeholder="Eg : 12-2-2025"
												format="DD-MM-YYYY"
												disabled={skipDocumentUpload}
											/>
										</Form.Item>
									</DatePickerWrapper>
								</InlineRow>
							</Form.Item>
							{/* Row 3: Vehicle RC Doc Upload + Valid Till | (empty) */}
							<Form.Item
								name="rc_doc_key"
								label="Vehicle RC Doc Upload"
								rules={[
									{
										required: !skipDocumentUpload,
										message: 'Please upload RC document',
									},
								]}
							>
								<InlineRow>
									<UploadButtonWrapper>
										<PrimaryBrowseFileButton
											icon={<UploadOutlined />}
											onClick={() =>
												handleBrowseFile('rc_doc', rcDocFileInputRef)
											}
											loading={uploading.rc_doc}
											disabled={skipDocumentUpload}
										>
											Browse File
										</PrimaryBrowseFileButton>
										{fileNames.rc_doc && (
											<FileDisplayContainer>
												<FileInfo>
													<FileIcon />
													<Text
														style={{
															fontSize: 14,
															color: '#374151',
														}}
													>
														Fileupload.Pdf{' '}
														{fileSizes.rc_doc &&
															formatFileSize(fileSizes.rc_doc)}
													</Text>
												</FileInfo>
												<RemoveFileButton
													onClick={() => handleRemoveFile('rc_doc')}
												>
													<CloseOutlined />
												</RemoveFileButton>
											</FileDisplayContainer>
										)}
										<HiddenFileInput
											type="file"
											ref={rcDocFileInputRef}
											accept=".pdf,.png,.jpg,.jpeg"
											onChange={(e) => handleFileChange(e, 'rc_doc')}
										/>
									</UploadButtonWrapper>
									<DatePickerWrapper>
										<Form.Item
											name="rc_expiry_date"
											label="Valid Till"
											rules={[
												{
													required: !skipDocumentUpload,
													message: VALID_TILL_DATE_MESSAGE,
												},
											]}
											style={{ marginBottom: 0 }}
										>
											<DatePicker
												style={{ width: '100%' }}
												placeholder="Eg : 12-2-2025"
												format="DD-MM-YYYY"
												disabled={skipDocumentUpload}
											/>
										</Form.Item>
									</DatePickerWrapper>
								</InlineRow>
							</Form.Item>
							{/* Row 3: Vehicle RC Doc Upload + Valid Till | PUC Doc Upload + Valid Till */}
							<Form.Item name="puc_doc_key" label="PUC Doc Upload">
								<InlineRow>
									<UploadButtonWrapper>
										<PrimaryBrowseFileButton
											icon={<UploadOutlined />}
											onClick={() =>
												handleBrowseFile('puc_doc', pucDocFileInputRef)
											}
											loading={uploading.puc_doc}
											disabled={skipDocumentUpload}
										>
											Browse File
										</PrimaryBrowseFileButton>
										{fileNames.puc_doc && (
											<FileDisplayContainer>
												<FileInfo>
													<FileIcon />
													<Text
														style={{
															fontSize: 14,
															color: '#374151',
														}}
													>
														Fileupload.Pdf{' '}
														{fileSizes.puc_doc &&
															formatFileSize(fileSizes.puc_doc)}
													</Text>
												</FileInfo>
												<RemoveFileButton
													onClick={() => handleRemoveFile('puc_doc')}
												>
													<CloseOutlined />
												</RemoveFileButton>
											</FileDisplayContainer>
										)}
										<HiddenFileInput
											type="file"
											ref={pucDocFileInputRef}
											accept=".pdf,.png,.jpg,.jpeg"
											onChange={(e) => handleFileChange(e, 'puc_doc')}
										/>
									</UploadButtonWrapper>
									<DatePickerWrapper>
										<Form.Item
											name="puc_expiry_date"
											label="Valid Till"
											required={isPucExpiryDateRequired}
											rules={[
												{
													validator: (_, value) => {
														if (!isPucExpiryDateRequired || value) {
															return Promise.resolve();
														}

														return Promise.reject(
															new Error(VALID_TILL_DATE_MESSAGE)
														);
													},
												},
											]}
											style={{ marginBottom: 0 }}
										>
											<DatePicker
												style={{ width: '100%' }}
												placeholder="Eg : 12-2-2025"
												format="DD-MM-YYYY"
												disabled={skipDocumentUpload}
											/>
										</Form.Item>
									</DatePickerWrapper>
								</InlineRow>
							</Form.Item>
							{/* Row 4: Owner Ship Type | Upload File (consent letter) */}
							<Form.Item name="ownership_type" label="Owner Ship Type">
								<Select placeholder="Select" disabled={skipDocumentUpload}>
									{OWNERSHIP_TYPE_OPTIONS.map((option) => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</Select>
							</Form.Item>
							<Form.Item
								name="consent_letter_key"
								label="Upload File"
								required={ownershipType === 'Aggregator' && !skipDocumentUpload}
								rules={[
									{
										validator: (_, value) => {
											const ownershipTypeValue =
												form.getFieldValue('ownership_type');
											if (
												ownershipTypeValue === 'Aggregator' &&
												!value &&
												!skipDocumentUpload
											) {
												return Promise.reject(
													new Error(
														'Please upload consent letter for Aggregator'
													)
												);
											}
											return Promise.resolve();
										},
									},
								]}
							>
								<InlineRow>
									<Input
										placeholder="Eg : File"
										value={fileNames.consent_letter || ''}
										disabled
										readOnly
										style={{ backgroundColor: '#f9fafb', flex: 1 }}
									/>
									<LightBrowseFileButton
										icon={<UploadOutlined />}
										onClick={() =>
											handleBrowseFile('consent_letter', consentFileInputRef)
										}
										loading={uploading.consent_letter}
										disabled={!ownershipType || skipDocumentUpload}
									>
										Browse File
									</LightBrowseFileButton>
								</InlineRow>
								{fileNames.consent_letter && (
									<FileDisplayContainer style={{ marginTop: 8 }}>
										<FileInfo>
											<FileIcon />
											<Text
												style={{
													fontSize: 14,
													color: '#374151',
												}}
											>
												Fileupload.Pdf{' '}
												{fileSizes.consent_letter &&
													formatFileSize(fileSizes.consent_letter)}
											</Text>
										</FileInfo>
										<RemoveFileButton
											onClick={() => handleRemoveFile('consent_letter')}
										>
											<CloseOutlined />
										</RemoveFileButton>
									</FileDisplayContainer>
								)}
								<HiddenFileInput
									type="file"
									ref={consentFileInputRef}
									accept=".pdf,.png,.jpg,.jpeg"
									onChange={(e) => handleFileChange(e, 'consent_letter')}
								/>
							</Form.Item>
							{/* Row 5: GPS Installed | GPS Provider Name */}
							<Form.Item
								name="gps_installed"
								label="GPS Installed"
								rules={[
									{
										required: !skipDocumentUpload,
										message: 'Please select GPS installation status',
									},
								]}
							>
								<Select placeholder="Select" disabled={skipDocumentUpload}>
									<Option value>Yes</Option>
									<Option value={false}>No</Option>
								</Select>
							</Form.Item>
							{gpsInstalled === true && !skipDocumentUpload && (
								<Form.Item
									name="gps_provider_name"
									label="GPS Provider Name"
									rules={[
										{
											required: true,
											message: 'Please enter GPS provider name',
										},
									]}
								>
									<Input placeholder="Enter Provider Name" />
								</Form.Item>
							)}
							<Form.Item label="Skip Document Upload">
								<SkipDocumentUploadRow>
									<SkipDocumentCheckbox
										checked={skipDocumentUpload}
										onChange={(e) => {
											setSkipDocumentUpload(e.target.checked);
											if (e.target.checked) {
												clearUploadedDocumentState();
											}
										}}
									>
										SKIP DOCUMENT UPLOAD
									</SkipDocumentCheckbox>
									<Tooltip
										title="A vehicle can be onboarded by skipping the documents for now.But a trip cannot be closed if the vehicle documents are not present."
										placement="right"
									>
										<SkipDocumentInfoIcon>i</SkipDocumentInfoIcon>
									</Tooltip>
								</SkipDocumentUploadRow>
							</Form.Item>
						</FormGrid>

						<AddVehicleButtonWrapper>
							<AddVehicleButton type="button" onClick={handleAddVehicle}>
								Add Vehicle
							</AddVehicleButton>
						</AddVehicleButtonWrapper>
					</StyledForm>
				</FormSection>

				<TableSection>
					<SectionTitle>Vehicle Details</SectionTitle>
					<StyledTable
						columns={tableColumns}
						dataSource={vehicleDetails}
						pagination={false}
						locale={{ emptyText: 'No vehicles added yet' }}
					/>
				</TableSection>

				<FooterActions>
					<CancelButton onClick={() => navigate('/vehicles')}>Cancel</CancelButton>
					<SubmitButton onClick={handleSubmit}>Submit</SubmitButton>
				</FooterActions>

				{/* Edit Vehicle Modal */}
				<EditVehicleModal
					visible={editModalVisible}
					vehicle={selectedVehicle}
					vendorOptions={vendorOptions}
					vehicleTypeOptions={vehicleTypeOptions}
					onClose={handleEditModalClose}
					onUpdate={handleUpdateVehicle}
					onRemove={handleRemoveVehicle}
				/>

				{/* Document Viewer Modal */}
				<DocumentViewerModal
					visible={documentModalVisible}
					onClose={handleDocumentModalClose}
					documentUrl={documentUrl}
					documentName={documentName}
					documentType={documentType}
				/>

				{/* Bulk Upload Modal */}
				<BulkUploadModal
					visible={bulkUploadModalVisible}
					onClose={handleBulkUploadClose}
					onSubmit={handleBulkUploadSubmit}
					onFileUpload={handleBulkUploadFileToS3}
					title="Bulk Upload Vehicles"
					description="Please upload a CSV file to upload all the vehicles info all at once."
					templateUrl="/vehicle_bulk_upload_template_v2.csv"
					templateText="Download the CSV template here:"
					acceptedFormats=".csv"
					maxSizeMB={10}
					loading={bulkUploadLoading}
				/>
			</ContentWrapper>
		</PageContainer>
	);
}
